import { DestroyRef, computed, inject } from '@angular/core';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { EMPTY, Subscription, catchError, of, retry, switchMap, tap, throwError, timer } from 'rxjs';
import { environment } from '@environments/environment';
import { BrandingService } from 'src/core/services/branding/branding.service';
import { BrandingConfig } from 'src/core/interfaces/branding.interfaces';
import { sanitizeColor } from 'src/utils/helpers/color-sanitizer';
import { isValidHttpsUrl } from 'src/utils/helpers/url-sanitizer';

export const DEFAULT_COLORS = { primary: '#1E40AF', secondary: '#10B981', accent: '#F59E0B' };
export const DEFAULT_LEGAL_NAME = 'Portal Cliente';
const RETRY_DELAY_MS = 1500;
// PWT-1055: los documentos legales se reintentan aparte del respaldo, con backoff acotado (LF.1 descarta el polling agresivo).
export const LEGAL_DOCUMENTS_RETRY_DELAYS_MS = [3000, 6000, 12000, 24000, 48000];
const CACHE_STORAGE_KEY = 'pc_branding_cache';

interface PersistedBranding {
  companyId: string;
  config: BrandingConfig;
  fetchedAt: number;
}

function readPersistedBranding(companyId: string): PersistedBranding | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_STORAGE_KEY) ?? 'null') as PersistedBranding | null;
    if (!parsed || parsed.companyId !== companyId || typeof parsed.fetchedAt !== 'number' || !parsed.config?.colorPalette) {
      return null;
    }
    const age = Date.now() - parsed.fetchedAt;
    return age >= 0 && age < environment.brandingCacheTtlMs ? parsed : null;
  } catch {
    return null;
  }
}

function persistBranding(entry: PersistedBranding): void {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    return;
  }
}

interface BrandingState {
  config: BrandingConfig | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  companyId: string | null;
  /** El branding no respondió y se pintó el respaldo de LF.1: no es un portal "no operativo". */
  offlineFallback: boolean;
}

const initialState: BrandingState = {
  config: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  companyId: null,
  offlineFallback: false,
};

// REQ-FUNC-11/12 — defensa en profundidad: re-valida todo antes de inyectar, aplica vía setProperty
function applyCssVariables(config: BrandingConfig): void {
  const root = document.documentElement;
  const primary = sanitizeColor(config.colorPalette.primary, DEFAULT_COLORS.primary);
  const secondary = sanitizeColor(config.colorPalette.secondary, DEFAULT_COLORS.secondary);
  const accent = sanitizeColor(config.colorPalette.accent, DEFAULT_COLORS.accent);
  // LF.1 declara los tokens en español; los ingleses quedan como alias para las plantillas existentes
  root.style.setProperty('--color-primario', primary);
  root.style.setProperty('--color-secundario', secondary);
  root.style.setProperty('--color-acento', accent);
  root.style.setProperty('--color-primary', primary);
  root.style.setProperty('--color-secondary', secondary);
  root.style.setProperty('--color-accent', accent);
}

function sanitizeConfig(config: BrandingConfig): BrandingConfig {
  return {
    ...config,
    legalName: (config.legalName || DEFAULT_LEGAL_NAME).replace(/<[^>]*>/g, '').slice(0, 100),
    logoUrl: config.logoUrl && isValidHttpsUrl(config.logoUrl) ? config.logoUrl : null,
    colorPalette: {
      primary: sanitizeColor(config.colorPalette?.primary, DEFAULT_COLORS.primary),
      secondary: sanitizeColor(config.colorPalette?.secondary, DEFAULT_COLORS.secondary),
      accent: sanitizeColor(config.colorPalette?.accent, DEFAULT_COLORS.accent),
    },
    country: config.country?.isoCode
      ? { isoCode: config.country.isoCode.trim().toUpperCase(), dialingCode: config.country.dialingCode ?? null }
      : null,
    legalDocuments: {
      termsUrl: isValidHttpsUrl(config.legalDocuments?.termsUrl) ? config.legalDocuments.termsUrl : '',
      privacyPolicyUrl: isValidHttpsUrl(config.legalDocuments?.privacyPolicyUrl) ? config.legalDocuments.privacyPolicyUrl : '',
      termsUrlUpdatedAt: config.legalDocuments?.termsUrlUpdatedAt ?? null,
      privacyPolicyUrlUpdatedAt: config.legalDocuments?.privacyPolicyUrlUpdatedAt ?? null,
    },
  };
}

export const BrandingStore = signalStore(
  { providedIn: 'root' },
  withState<BrandingState>(initialState),
  withComputed((store) => ({
    isPortalRestricted: computed(() => {
      const config = store.config();
      if (!config || store.offlineFallback()) return false;
      // ORG.3 acepta `http://` como formato válido: el saneador deshabilita el link, pero no restringe el portal.
      if (typeof config.portalOperational === 'boolean') return !config.portalOperational;
      return !config.legalDocuments.termsUrl || !config.legalDocuments.privacyPolicyUrl;
    }),
  })),
  withMethods((store, service = inject(BrandingService)) => {
    let legalDocumentsRetry: Subscription | null = null;
    inject(DestroyRef).onDestroy(() => legalDocumentsRetry?.unsubscribe());

    const applyFetched = (companyId: string, config: BrandingConfig): void => {
      const sanitized = sanitizeConfig(config);
      const fetchedAt = Date.now();
      applyCssVariables(sanitized);
      persistBranding({ companyId, config: sanitized, fetchedAt });
      patchState(store, { config: sanitized, isLoading: false, lastFetchedAt: fetchedAt, error: null, offlineFallback: false });
    };

    const retryLegalDocuments = (companyId: string): void => {
      legalDocumentsRetry?.unsubscribe();
      let attempt = 0;
      legalDocumentsRetry = timer(LEGAL_DOCUMENTS_RETRY_DELAYS_MS[0])
        .pipe(
          switchMap(() =>
            service.get(companyId).pipe(
              retry({
                count: LEGAL_DOCUMENTS_RETRY_DELAYS_MS.length - 1,
                delay: () => timer(LEGAL_DOCUMENTS_RETRY_DELAYS_MS[++attempt]),
              })
            )
          ),
          tap((config) => {
            if (store.offlineFallback() && store.companyId() === companyId) applyFetched(companyId, config);
          }),
          catchError(() => EMPTY)
        )
        .subscribe();
    };

    return {
      load: (companyId: string) => {
        legalDocumentsRetry?.unsubscribe();
        const persisted = readPersistedBranding(companyId);
        if (persisted) {
          const sanitized = sanitizeConfig(persisted.config);
          applyCssVariables(sanitized);
          patchState(store, {
            config: sanitized,
            isLoading: false,
            error: null,
            companyId,
            lastFetchedAt: persisted.fetchedAt,
            offlineFallback: false,
          });
          return of(sanitized);
        }

        patchState(store, { isLoading: true, error: null, companyId, offlineFallback: false });
        return service.get(companyId).pipe(
          // Un fallo de red no debería condenar al tenant a la paleta default (REQ-ERR-03).
          retry({ count: 1, delay: (error) => (error?.status === 0 ? timer(RETRY_DELAY_MS) : throwError(() => error)) }),
          tap((config) => applyFetched(companyId, config)),
          catchError(() => {
            const fallback: BrandingConfig = {
              ...sanitizeConfig({
                legalName: DEFAULT_LEGAL_NAME,
                logoUrl: null,
                colorPalette: DEFAULT_COLORS,
                legalDocuments: { termsUrl: '', privacyPolicyUrl: '', termsUrlUpdatedAt: null, privacyPolicyUrlUpdatedAt: null },
                footerMessage: '',
                supportPhone: '',
                portalOperational: true,
                country: null,
              }),
              legalName: '',
            };
            applyCssVariables(fallback);
            // LF.2 §Contingencia: el respaldo se aplica sin aviso de error porque la pantalla sigue operable.
            patchState(store, {
              config: fallback,
              isLoading: false,
              error: 'No se pudo cargar el branding del tenant.',
              lastFetchedAt: Date.now(),
              offlineFallback: true,
            });
            retryLegalDocuments(companyId);
            return of(fallback);
          })
        );
      },

      // Refetch en cada route change solo si expiró el TTL (60s). Sin WS/SSE/polling.
      refreshIfStale: () => {
        const companyId = store.companyId();
        const lastFetchedAt = store.lastFetchedAt();
        if (!companyId || store.isLoading()) return;
        const isStale = !lastFetchedAt || Date.now() - lastFetchedAt >= environment.brandingCacheTtlMs;
        if (!isStale) return;

        service.get(companyId).pipe(
          tap((config) => applyFetched(companyId, config)),
          catchError(() => EMPTY)
        ).subscribe();
      },
    };
  })
);
